import { ApolloError } from '@apollo/client';
import { addDays, format } from 'date-fns';

import { BillingFrequency } from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';

export const DEFAULT_PAGE_SIZE = 10;

/**
 * Extracts a human-readable cause from a backend (Apollo) mutation error.
 *
 * Backend rejections surface as GraphQL errors where the top-level `message` is
 * just the error code ("INVALID_INPUT") and the human sentence lives in
 * `extensions.exception.reason`. We surface both as "CODE: reason" so the toast
 * shows the original backend message and code; returns undefined if nothing
 * useful can be extracted.
 */
export function getBackendErrorDetail(err: unknown): string | undefined {
  if (!(err instanceof ApolloError)) {
    return err instanceof Error && err.message ? err.message : undefined;
  }

  const gqlErr = err.graphQLErrors?.[0];
  const ext = gqlErr?.extensions as
    | { code?: unknown; exception?: { reason?: unknown; error?: unknown } }
    | undefined;
  const exception = ext?.exception;

  const code =
    (typeof exception?.error === 'string' && exception.error) ||
    (typeof gqlErr?.message === 'string' && gqlErr.message) ||
    (typeof ext?.code === 'string' && ext.code) ||
    undefined;
  const reason =
    typeof exception?.reason === 'string' && exception.reason.trim()
      ? exception.reason.trim()
      : undefined;

  if (code && reason) return `${code}: ${reason}`;
  return reason || code || err.message || undefined;
}

/**
 * billing_end is stored and sent to the backend EXCLUSIVELY: it is the first day
 * that is NOT billed (the backend clips the billing window to `[start, end)`).
 * The asset modal, however, lets the user pick the inclusive "Last day of
 * billing". These two helpers convert between the two representations at the
 * form boundary so the picker stays intuitive while the backend contract is
 * honoured. They are exact inverses — keep them that way.
 */

/** Backend value (exclusive, ISO) → inclusive last-billed day shown in the picker. */
export function billingEndToLastBilledDay(billingEndIso: string): Date {
  // Parse the calendar day from the ISO date portion (not the instant) so a
  // positive-offset timezone like Berlin doesn't shift it, then step back one
  // day: the stored end is the first UN-billed day.
  const [year, month, day] = billingEndIso.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day - 1);
}

/** Inclusive last-billed day from the picker → backend value (exclusive, yyyy-MM-dd). */
export function lastBilledDayToBillingEnd(lastBilledDay: Date): string {
  // The day after the last billed day is the first un-billed day. Use yyyy-MM-dd
  // (not toISOString) so the local calendar day isn't shifted by the UTC offset.
  return format(addDays(lastBilledDay, 1), 'yyyy-MM-dd');
}

export type PageItem = number | 'ellipsis';

/**
 * Generates pagination items with ellipsis for large page counts.
 * Shows first, last, and a window around current page.
 */
export function getPaginationItems(
  currentPage: number,
  totalPages: number,
): PageItem[] {
  if (totalPages <= 1) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // Always show: first, last, and a small window around current page.
  if (currentPage <= 4) return [1, 2, 3, 4, 5, 'ellipsis', totalPages];
  if (currentPage >= totalPages - 3) {
    return [
      1,
      'ellipsis',
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    'ellipsis',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    'ellipsis',
    totalPages,
  ];
}

/**
 * Formats billing frequency for display using i18n.
 */
export function formatBillingFrequency(
  frequency: BillingFrequency | null | undefined,
): string {
  switch (frequency) {
    case BillingFrequency.Monthly:
      return i18n.t('ItemType:billingFrequencyMonthly');
    case BillingFrequency.Yearly:
      return i18n.t('ItemType:billingFrequencyYearly');
    case BillingFrequency.OneTime:
      return i18n.t('ItemType:billingFrequencyOneTime');
    default:
      return '-';
  }
}
